import { t } from 'i18next';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { getSegmentCurve } from '../../../../../../elements/design/design-node/utils/conic';
import {
  getLineSegmentFaces,
  LineSegmentId,
  recalculateLineSegmentMetaData,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { ActionInProgress, ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { addAndCommitAction } from '../../utils/actions';
import {
  addArcLineSegments,
  Arc,
  canHaveArcMeasure,
  CurveShape,
  getAlignedShape,
  getArcBulge,
  getArcCurves,
  getArcMeasure,
  getConicCursorVertices,
  getCurveArcs,
  getCurveThroughPoint,
  getLocalShape,
} from '../../utils/curve';
import { abortActionInProgress, applyActionInProgress, initActionInProgress } from '../general';

/**
 * Line segments that are part of faces can not be curved, since the faces would not follow the curve.
 */
export const canCurveLineSegment = (tool: DesignTool, id: LineSegmentId) =>
  getLineSegmentFaces(tool.asset.designNode, id).length === 0;

/**
 * Curves a line segment along the arcs, in local coordinates, as an undoable action, or makes it
 * straight when there are none. A curve in several pieces, of several arcs or an arc too large for
 * one curve, replaces the line segment with a line segment per piece, with points between them.
 */
function setCurves(tool: DesignTool, id: LineSegmentId, arcs: Arc[]) {
  const node = tool.asset.designNode;
  const conics = arcs.length === 1 ? getArcCurves(arcs[0].start, arcs[0].end, arcs[0].bulge) : [];
  let changes: RevertChange[] = [];

  addAndCommitAction(tool, {
    commit: () => {
      if (arcs.length > 1 || conics.length > 1) {
        const segment = node.getLineSegmentById(id);
        changes = [node.removeLineSegment(id)];
        changes.unshift(
          ...addArcLineSegments(
            node,
            segment.values[0],
            segment.values[1],
            arcs,
            segment.attributes,
            {
              width: segment.width,
              stripe: segment.stripe,
            },
          ),
        );
      } else {
        changes = [node.upsertLineSegment({ id, curve: conics[0] && getSegmentCurve(conics[0]) })];
        recalculateLineSegmentMetaData(node, [id]);
      }

      node.refresh();
      tool.asset.refreshMetadata();
    },
    rollback: () => {
      node.revert(changes);
      node.refresh();
      tool.asset.refreshMetadata();
    },
  });
}

/**
 * Bends a line segment so that it passes through the cursor, the start and end stay in place.
 * Its radius or bulge can also be typed in the tooltip, then the cursor only chooses the side.
 * Holding Shift, it follows the lines at its ends instead, see getAlignedShape.
 */
export const bendLine = (tool: DesignTool, id: LineSegmentId): ContextMenuItem => {
  const disabled = !canCurveLineSegment(tool, id);

  return {
    title: t('contextMenu.bendLine', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      const node = tool.asset.designNode;
      const curveCursor = new PickingLine();

      const getThroughPoint = () => {
        const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
        return getCurveThroughPoint(tool, (start.z + end.z) / 2);
      };

      const measure = tool.curveMeasure;
      // The radius or bulge (meters) of the curve, see CurveMeasure, when it is typed.
      let typedDistance: number | undefined;

      /**
       * The shape of the curve, in world coordinates: a circular arc through the cursor, or with
       * the typed distance, or one that follows the lines at its ends while Shift is held.
       */
      const getShape = (): CurveShape | undefined => {
        const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
        const through = getThroughPoint();

        if (!through && !isDefined(typedDistance)) {
          return undefined;
        }

        const bulge = getArcBulge(
          start,
          end,
          through,
          isDefined(typedDistance) ? { measure, distance: typedDistance } : undefined,
        );
        const [startPointId, endPointId] = node.getLineSegmentById(id).values;

        return (
          (tool.inputHandler.shiftIsDown &&
            getAlignedShape(
              node,
              { position: start, pointId: startPointId },
              { position: end, pointId: endPointId },
              bulge,
            )) || { bulge }
        );
      };

      /**
       * The arcs of the curve, in the coordinates of the mode.
       */
      const getArcs = (mode: 'local' | 'world') => {
        const shape = getShape();
        const [start, end] = node.getVerticesByLineSegmentId(id, mode);
        return (
          shape && getCurveArcs(start, end, mode === 'local' ? getLocalShape(node, shape) : shape)
        );
      };

      const action: ActionInProgress = {
        actionName: ActionName.BendCurve,
        initiate: () => {
          tool.add(curveCursor);
          tool.curveDistanceInput = {
            kind: measure,
            // Of the first arc, when the curve follows the lines at both its ends
            getDistance: () => {
              const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
              const [arc] = getArcs('world') ?? [{ start, end, bulge: 0 }];
              return arc ? getArcMeasure(arc.start, arc.end, arc.bulge, measure) : Infinity;
            },
            setDistance: (distance) => {
              typedDistance = distance;
              action.refresh?.();
              tool.updateTooltip();
            },
            isValid: (distance) => {
              const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
              return canHaveArcMeasure(start, end, distance, measure);
            },
            apply: () => void applyActionInProgress(tool).action(),
            cancel: () => void abortActionInProgress(tool).action(),
          };
          // Shows the distance in the tooltip right away, where it can be typed.
          tool.updateTooltip();
          tool.onEdited?.();
        },
        cleanup: () => {
          tool.remove(curveCursor);
          tool.curveDistanceInput = undefined;
        },
        refresh: () => {
          const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
          const through = getThroughPoint();

          // Show the cursor where the curve passes through, unless the distance is typed.
          if (through && !isDefined(typedDistance)) {
            tool.cursorPoint.position.copy(through);
          }

          const conics = (getArcs('world') ?? []).flatMap((arc) =>
            getArcCurves(arc.start, arc.end, arc.bulge),
          );
          curveCursor.setVertices(
            conics.length > 0 ? getConicCursorVertices(conics) : [start, end],
          );
        },
        readyToApply: () => isDefined(getShape()),
        apply: () => {
          const arcs = getArcs('local');

          if (isDefined(arcs)) {
            setCurves(tool, id, arcs);
          }
        },
      };

      initActionInProgress(tool, action);
      tool.contextMenuCallback?.();
    },
    disabled,
  };
};

/**
 * Makes a curved line segment straight.
 */
export const straightenLine = (tool: DesignTool, id: LineSegmentId): ContextMenuItem => {
  return {
    title: t('contextMenu.straightenLine', { ns: 'skyviewDesign' }),
    action: () => {
      if (isDefined(tool.asset.designNode.getLineSegmentById(id).curve)) {
        setCurves(tool, id, []);
      }

      tool.contextMenuCallback?.();
    },
  };
};
